/// <reference lib="esnext" />

import { COMMENT_NODE, DOCUMENT_FRAGMENT_NODE, ELEMENT_NODE, flatMap, ieval, TEXT_NODE, VElement, VNode, withFunc } from "./common.ts"

const interpolate = (str: string, data: unknown) =>
  str.replace(/\[\|(.*)\|\]/g, (_, expr) => ieval(expr, data))

export type TemplateDirective = (o: {
  modifiers: string[],
  value: string,
  element: Element,
  data: Record<string, any>,
  render: (d: Record<string, any>) => VNode[],
}) => VNode[]

export const templateDirectives = new Map<string, TemplateDirective>()

export const render = (
  template: Node,
  data: Record<string, any>,
  donelist: Attr[] = [],
): VNode[] => {
  let { nodeType, nodeValue } = template
  if (nodeType === TEXT_NODE || nodeType === COMMENT_NODE)
    return [{ nodeType, nodeValue: interpolate(nodeValue!, data) }]
  
  if (nodeType === DOCUMENT_FRAGMENT_NODE)
    return flatMap(template.childNodes, n => render(n, data))
  
  if (nodeType === ELEMENT_NODE) {
    let element = template as Element
    let { tagName } = element
    let attributes = {} as Record<string, string>
    let properties = {} as Record<string, unknown>

    for (let attr of element.attributes) {
      let { name, value } = attr
      if (name.startsWith('p:')) {
        if (donelist.includes(attr)) continue
        let donelist_ = donelist.concat(attr)
        let [, directive, ...modifiers] = name.split(':')
        let dir = templateDirectives.get(directive)
        if (dir) return dir({
          modifiers,
          value,
          element,
          data,
          render: element instanceof HTMLTemplateElement
            ? (data => flatMap(
                element.content.childNodes,
                element => render(element, data, [])
              ) as VNode[])
            : (data => render(element, data, donelist_)),
        })
      }
      else attributes[name] = interpolate(value, data)
    }
    
    let childNodes = flatMap(element.childNodes, n => render(n, data))
    
    return [{ nodeType, tagName, attributes, properties, childNodes, inits: [] }]
  }

  throw new Error(`Unsupported node type: ${nodeType}`)
}

templateDirectives.set('for', ({
  render, data, value,
  modifiers: [vvar, kvar]
}) => {
  let iterable = ieval(value, data)
  return Symbol.iterator in iterable
    ? flatMap(iterable, (v, k) =>
        render({ __proto__: data, [vvar]: v, [kvar]: k }))
    : Object.entries(iterable).flatMap(([k, v]) =>
        render({ __proto__: data, [vvar]: v, [kvar]: k }))
})

templateDirectives.set('if', ({ render, data, value }) =>
  ieval(value, data) ? render(data) : [])

templateDirectives.set('on', ({ render, data, value, modifiers: [eventType] }) =>
  render(data).map(vn => {
    (vn as VElement).properties['on' + eventType] = withFunc(value, 'event').bind(data)
    return vn
  }))

templateDirectives.set('set', ({ render, data, value, modifiers: [prop] }) =>
  render(data).map(vn => {
    (vn as VElement).properties[prop] = ieval(value, data)
    return vn
  }))

templateDirectives.set('init', ({ render, data, value }) =>
  render(data)
    .map(vn => ((vn as VElement).inits.push(withFunc(value, 'element').bind(data)), vn)))

templateDirectives.set('ref', ({ render, data, value }) =>
  render(data)
    .map(vn => ((vn as VElement).inits.push(
      el => data[value] = el
    ), vn)))

templateDirectives.set('data', ({ render, data, value }) =>
  render(Object.assign(Object.create(data), ieval(value || '{}', data))))
